// Attribute lists (§5.2): `{id=x class="a,b" title="A title"}`, with `{#x .a .b}` as sugar.
//   attribute = key ("=" (quoted | bare))? | "#" id | "." class
// A quoted value is a JSON string on one line; a bare one is anything but whitespace, braces and
// quotes; a bare key is a flag (true). Values are typed by the kind's registry entry: booleans
// take only true/false, integers -?[0-9]+, numbers -?[0-9]+(.[0-9]+)?, lists split on commas.
// No value is executable.
import { PATTERNS, type AttrSpec } from "./registry";

export type AttrValue = string | number | boolean | string[];
export type Attrs = Record<string, AttrValue>;

/** How a key is typed: its spec, or undefined when the kind does not declare it. */
export type SpecOf = (key: string) => AttrSpec | undefined;

export interface ParsedAttributes {
  attrs: Attrs;
  /** Each problem, in reading order: a malformed list, a repeated or unknown key, a mistyped value. */
  problems: string[];
}

const KEY = /^[a-z][a-z0-9_.-]*/;
const SHORT = /^[A-Za-z0-9][A-Za-z0-9._-]*/;
const BARE = /^[^\s{}"]+/;

/**
 * Parse the text between an attribute list's braces. `strict`: an undeclared key (other than an
 * `x-*` annotation) is a problem — the core kinds; an extension's or unknown kind's keys are kept
 * as written, since only its manifest could type them.
 */
export function parseAttributes(inner: string, specOf: SpecOf, strict = true): ParsedAttributes {
  const attrs: Attrs = {};
  const problems: string[] = [];
  const classes: string[] = [];
  let i = 0;
  const seen = (key: string) => {
    if (Object.hasOwn(attrs, key) || (key === "class" && classes.length)) { problems.push(`the attribute "${key}" is repeated`); return true; }
    return false;
  };
  while (i < inner.length) {
    while (i < inner.length && (inner[i] === " " || inner[i] === "\t")) i++;
    if (i >= inner.length) break;
    const rest = inner.slice(i);
    if (rest[0] === "#" || rest[0] === ".") {
      const m = SHORT.exec(rest.slice(1));
      if (!m) { problems.push(`"${rest.split(/[ \t]/)[0]}" is not a valid ${rest[0] === "#" ? "id" : "class"} shorthand`); break; }
      i += 1 + m[0].length;
      if (rest[0] === ".") {
        if (Object.hasOwn(attrs, "class")) problems.push(`the attribute "class" is repeated`);
        classes.push(m[0]);
      } else if (!seen("id")) attrs.id = m[0];
    } else {
      const k = KEY.exec(rest);
      if (!k) { problems.push(`"${rest.split(/[ \t]/)[0]}" is not an attribute: keys are lower-case, as in key=value`); break; }
      const key = k[0];
      i += key.length;
      let raw: string | true = true;
      if (inner[i] === "=") {
        i++;
        if (inner[i] === '"') {
          const end = quotedEnd(inner, i);
          if (end < 0) { problems.push(`the value of "${key}" has an unclosed quote`); break; }
          try { raw = JSON.parse(inner.slice(i, end)) as string; } catch { problems.push(`the value of "${key}" is not a valid quoted string`); break; }
          i = end;
        } else {
          const b = BARE.exec(inner.slice(i));
          if (!b) { problems.push(`"${key}=" has no value`); break; }
          raw = b[0];
          i += b[0].length;
        }
      }
      if (!seen(key)) {
        const typed = typeValue(key, raw, specOf(key), strict);
        if (typeof typed === "object" && typed !== null && "problem" in typed) problems.push(typed.problem);
        else attrs[key] = typed as AttrValue;
      }
    }
    if (i < inner.length && inner[i] !== " " && inner[i] !== "\t") {
      problems.push(`attributes are separated by spaces: "${inner.slice(i).split(/[ \t]/)[0]}" follows without one`);
      break;
    }
  }
  if (classes.length && !Object.hasOwn(attrs, "class")) attrs.class = classes;
  return { attrs, problems };
}

/** Where a JSON string starting at `start` (a quote) ends, exclusive; -1 when it does not close. */
export function quotedEnd(s: string, start: number): number {
  for (let j = start + 1; j < s.length; j++) {
    if (s[j] === "\\") { j++; continue; }
    if (s[j] === '"') return j + 1;
  }
  return -1;
}

/** Where an attribute list opening at `start` (`{`) closes on its line, exclusive; -1 when it does not. */
export function attributesEnd(s: string, start: number): number {
  for (let j = start + 1; j < s.length; j++) {
    const c = s[j];
    if (c === "\n" || c === "\r") return -1;
    if (c === '"') { const e = quotedEnd(s, j); if (e < 0) return -1; j = e - 1; continue; }
    if (c === "}") return j + 1;
    if (c === "{") return -1;
  }
  return -1;
}

function typeValue(key: string, raw: string | true, spec: AttrSpec | undefined, strict: boolean): AttrValue | { problem: string } {
  if (!spec) {
    if (!strict || key.startsWith("x-")) return raw;
    return { problem: `"${key}" is not an attribute this kind takes` };
  }
  const type = spec.type;
  if (spec.enum) {
    if (raw === true || !spec.enum.includes(raw)) return { problem: `"${key}" is one of ${spec.enum.join(", ")}` };
    return raw;
  }
  if (type === "boolean") {
    if (raw === true) return true;
    if (raw === "true" || raw === "false") return raw === "true";
    return { problem: `"${key}" is true or false` };
  }
  if (raw === true) return type === undefined ? true : { problem: `"${key}" needs a value` };
  if (type === "integer" || type === "number") {
    const re = type === "integer" ? /^-?[0-9]+$/ : /^-?[0-9]+(?:\.[0-9]+)?$/;
    if (!re.test(raw)) return { problem: `"${key}" is ${type === "integer" ? "an integer" : "a number"}` };
    // (a number past what a double holds is not one: canonical JSON, a digest, has no Infinity)
    const n = Number(raw);
    if (!Number.isFinite(n)) return { problem: `"${key}" is ${type === "integer" ? "an integer" : "a number"}` };
    if (spec.minimum !== undefined && n < spec.minimum) return { problem: `"${key}" is at least ${spec.minimum}` };
    if (spec.maximum !== undefined && n > spec.maximum) return { problem: `"${key}" is at most ${spec.maximum}` };
    return n;
  }
  if (type === "array") {
    const list = raw.split(",").map((s) => s.replace(/^ +| +$/g, ""));
    if (list.some((s) => s === "")) return { problem: `"${key}" is a comma-separated list without empty members` };
    return list;
  }
  if (spec.pattern && !new RegExp(spec.pattern).test(raw)) {
    return { problem: key === "id" || spec.pattern === PATTERNS.id.source ? `"${raw}" is not a valid ${key}: use letters, digits, ".", "_" and "-"` : `"${key}" does not have the expected form` };
  }
  return raw;
}
